import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Button, Card, Typography, Space, Divider, message, Spin } from 'antd';
import StandardPage from '../components/Layout/StandardPage';
import { getMe, updateUser, type UserDTO } from '../services/users';
import { SaveOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ProfilePage: React.FC = () => {
  const [form] = Form.useForm<{
    firstName: string;
    lastName: string;
    email: string;
    newPassword?: string;
    confirmPassword?: string;
  }>();
  const [me, setMe] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const user = await getMe();
      setMe(user);
      form.setFieldsValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
    } catch (e: any) {
      message.error(e?.message || 'Errore nel caricamento profilo');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => { load(); }, [load]);

  const onFinish = async (values: any) => {
    if (!me) return;
    try {
      setSaving(true);
      const payload: any = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      };
      if (values.newPassword) {
        payload.password = values.newPassword; // il backend accetta "password"
      }
      const updated = await updateUser(me.id, payload);
      setMe(updated);
      // pulisco i campi password
      form.resetFields(['newPassword', 'confirmPassword']);
      message.success('Profilo aggiornato con successo');
    } catch (e: any) {
      message.error(e?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const actions = [
    {
      label: 'Salva',
      type: 'primary' as const,
      icon: <SaveOutlined />,
      loading: saving,
      onClick: () => form.submit(),
    },
  ];

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Profilo' },
  ];

  if (loading) {
    return (
      <StandardPage title="Profilo" breadcrumbs={breadcrumbs}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Caricamento profilo…</Text>
          </div>
        </div>
      </StandardPage>
    );
  }

  return (
    <StandardPage
      title="Il mio profilo"
      subtitle="Aggiorna i tuoi dati personali e la password"
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark="optional"
      >
        {/* Dati account */}
        <Card title="Informazioni account" style={{ marginBottom: 24 }}>
          <Form.Item
            label="Nome"
            name="firstName"
            rules={[{ required: true, message: 'Inserisci il nome' }]}
          >
            <Input placeholder="Mario" />
          </Form.Item>

          <Form.Item
            label="Cognome"
            name="lastName"
            rules={[{ required: true, message: 'Inserisci il cognome' }]}
          >
            <Input placeholder="Rossi" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Inserisci la tua email' },
              { type: 'email', message: 'Email non valida' },
            ]}
          >
            <Input placeholder="mario.rossi@azienda.it" />
          </Form.Item>

          {me?.companyName && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Azienda: <strong>{me.companyName}</strong>
              </Text>
            </div>
          )}
        </Card>

        {/* Password */}
        <Card title="Modifica password">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">
              Lascia vuoti i campi qui sotto se non vuoi cambiare password.
            </Text>

            <Form.Item
              label="Nuova password"
              name="newPassword"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve(); // opzionale
                    if (String(value).length < 8) {
                      return Promise.reject(new Error('Minimo 8 caratteri'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password placeholder="Nuova password" />
            </Form.Item>

            <Form.Item
              label="Conferma nuova password"
              name="confirmPassword"
              dependencies={['newPassword']}
              hasFeedback
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const newPwd = getFieldValue('newPassword');
                    if (!newPwd && !value) return Promise.resolve(); // entrambi vuoti = ok
                    if (value !== newPwd) return Promise.reject(new Error('Le password non coincidono'));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Ripeti nuova password" />
            </Form.Item>
          </Space>

          <Divider />

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={load}>Ripristina</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
              Salva modifiche
            </Button>
          </Space>
        </Card>
      </Form>
    </StandardPage>
  );
};

export default ProfilePage;
